import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    SelectChangeEvent,
    Box,
    Typography,
    Chip,
    IconButton,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { LeaveService } from '../../services/leave.service';
import { DocumentRequirement, LeaveTypeConfig, UserLeaveBalanceSummary } from '../../interfaces/leave.interface';
import { LeaveTypeService } from '../../services/leave-type.service';
import { LeaveBalanceService } from '../../services/leave-balance.service';
import { format, differenceInCalendarDays } from 'date-fns';
import { useToast } from '../../context/ToastContext';
import { TEXT_AREA_MAX_LENGTH } from '../../constants/fieldLimits';
import { useTranslation } from 'react-i18next';

interface LeaveFormProps {
    open: boolean;
    onClose: () => void;
    onSubmitSuccess: () => void;
}

const LeaveForm: React.FC<LeaveFormProps> = ({ open, onClose, onSubmitSuccess }) => {
    const defaultDate = format(new Date(), 'yyyy-MM-dd');
    const [leaveTypes, setLeaveTypes] = useState<LeaveTypeConfig[]>([]);
    const [formData, setFormData] = useState({
        leaveType: '',
        leaveReason: '',
        startDate: defaultDate,
        endDate: defaultDate,
        institutionId: '',
    });
    const [files, setFiles] = useState<File[]>([]);
    const [balanceSummary, setBalanceSummary] = useState<UserLeaveBalanceSummary | null>(null);
    const toast = useToast();
    const { t } = useTranslation(['leave', 'common']);

    const selectedLeaveType = leaveTypes.find(lt => lt.name === formData.leaveType);
    const docRequirement = selectedLeaveType?.documentRequirement || DocumentRequirement.NONE;

    const isDateRangeInvalid = !!(formData.startDate && formData.endDate
        && new Date(formData.endDate) < new Date(formData.startDate));

    const numberOfDays = formData.startDate && formData.endDate && !isDateRangeInvalid
        ? differenceInCalendarDays(new Date(formData.endDate), new Date(formData.startDate)) + 1
        : 0;

    const selectedBalance = balanceSummary?.balances.find(
        b => b.leaveType.toLowerCase() === formData.leaveType.toLowerCase()
    );

    useEffect(() => {
        LeaveTypeService.getByInstitution(true).then((types) => {
            setLeaveTypes(types);
            if (types.length > 0) {
                setFormData(prev => ({ ...prev, leaveType: types[0].name }));
            }
        }).catch(() => {});
        LeaveBalanceService.getMyBalance().then(setBalanceSummary).catch(() => {});
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (name === 'leaveType') {
            setFiles([]);
        }
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            setFiles(prev => [...prev, ...Array.from(e.target.files!)]);
        }
        e.target.value = '';
    };

    const handleRemoveFile = (index: number) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (new Date(formData.endDate) < new Date(formData.startDate)) {
            toast.showError(t('leaveForm.errors.endBeforeStart'));
            return;
        }

        if (selectedBalance && numberOfDays > selectedBalance.remaining) {
            toast.showError(
                t('leaveForm.errors.insufficientBalance', {
                    leaveType: formData.leaveType,
                    remaining: selectedBalance.remaining,
                    requested: numberOfDays,
                })
            );
            return;
        }

        if (docRequirement === DocumentRequirement.MANDATORY && files.length === 0) {
            toast.showError(t('leaveForm.errors.documentsRequired'));
            return;
        }

        try {
            const submitData = {
                ...formData,
                startDate: new Date(formData.startDate).toISOString(),
                endDate: new Date(formData.endDate).toISOString()
            };
            await LeaveService.createLeaveApplication(submitData, files.length > 0 ? files : undefined);
            onSubmitSuccess();
            onClose();
            setFiles([]);
        } catch (err) {
            toast.showApiError(err, t('leaveForm.errors.submit'));
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{t('leaveForm.title')}</DialogTitle>
            <form onSubmit={handleSubmit}>
                <DialogContent>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel id="leave-type-label">{t('leaveForm.leaveTypeLabel')}</InputLabel>
                        <Select
                            labelId="leave-type-label"
                            name="leaveType"
                            value={formData.leaveType}
                            label={t('leaveForm.leaveTypeLabel')}
                            onChange={handleChange}
                        >
                            {leaveTypes.map((lt) => (
                                <MenuItem key={lt.id} value={lt.name}>{lt.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        fullWidth
                        label={t('leaveForm.startDate')}
                        type="date"
                        name="startDate"
                        value={formData.startDate}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: true }}
                        sx={{ mb: 2 }}
                        required
                    />

                    <TextField
                        fullWidth
                        label={t('leaveForm.endDate')}
                        type="date"
                        name="endDate"
                        value={formData.endDate}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: formData.startDate }}
                        error={isDateRangeInvalid}
                        helperText={isDateRangeInvalid ? t('leaveForm.endDateBeforeStart') : ''}
                        sx={{ mb: 2 }}
                        required
                    />

                    {numberOfDays > 0 && (
                        <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                            <Chip
                                label={t('leaveForm.days', { count: numberOfDays })}
                                color="primary"
                                variant="outlined"
                            />
                            {selectedBalance && (
                                <Typography variant="body2" color={selectedBalance.remaining < numberOfDays ? 'error' : 'text.secondary'}>
                                    {t('leaveForm.remaining', { remaining: selectedBalance.remaining })}
                                </Typography>
                            )}
                        </Box>
                    )}

                    <TextField
                        fullWidth
                        label={t('leaveForm.reason')}
                        name="leaveReason"
                        value={formData.leaveReason}
                        onChange={handleChange}
                        multiline
                        rows={4}
                        required
                        inputProps={{ maxLength: TEXT_AREA_MAX_LENGTH }}
                        helperText={`${(formData.leaveReason || '').length}/${TEXT_AREA_MAX_LENGTH}`}
                    />

                    {docRequirement !== DocumentRequirement.NONE && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                {docRequirement === DocumentRequirement.MANDATORY
                                    ? t('leaveForm.documentsRequired')
                                    : t('leaveForm.documentsOptional')}
                            </Typography>
                            <Button
                                variant="outlined"
                                component="label"
                                startIcon={<AttachFileIcon />}
                                size="small"
                            >
                                {t('leaveForm.attachFiles')}
                                <input
                                    type="file"
                                    hidden
                                    multiple
                                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                    onChange={handleFileSelect}
                                />
                            </Button>
                            {files.length > 0 && (
                                <List dense sx={{ mt: 1 }}>
                                    {files.map((file, index) => (
                                        <ListItem key={index}>
                                            <ListItemText
                                                primary={file.name}
                                                secondary={`${(file.size / 1024).toFixed(1)} KB`}
                                            />
                                            <ListItemSecondaryAction>
                                                <IconButton edge="end" size="small" onClick={() => handleRemoveFile(index)} aria-label={t('leaveForm.removeFileAriaLabel')}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>{t('common:actions.cancel')}</Button>
                    <Button type="submit" variant="contained" color="primary" disabled={isDateRangeInvalid}>
                        {t('common:actions.submit')}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default LeaveForm;
